/**
 * Procedural trash-piece geometry (LOCKED-DESIGN 18.27 — the trash identity
 * pass). The five kinds stop being flat-color primitives and become
 * recognizable facility objects: a slatted crate, a ribbed steel can, a
 * panel-seamed bounce orb, a collared hazard orb, and the charred husk the
 * hazard orb becomes.
 *
 * DESIGN RULES, in priority order:
 *
 * 1. **Visual-only.** Physics never comes from a mesh (AGENTS.md): the collider
 *    envelopes in `Trash.spawnAt` (cuboid 0.425 / cylinder 0.4×0.35 / ball
 *    0.45 / ball 0.48) are byte-untouched, and every merged geometry here must
 *    fit INSIDE its kind's envelope — parts may reach the surface but never
 *    exceed it, so a restyle can never make pieces visually interpenetrate the
 *    sealed room. `TRASH_COLLIDER_ENVELOPES` states the caps (and spawnAt consumes them) and
 *    `TrashModels.test.ts` measures every vertex against them.
 *
 * 2. **Form carries meaning (18.1).** green vs red previously differed by tint
 *    alone, which fails the colorblind rule. Now the shapes disagree: green is
 *    a seamed ball with an equator gasket (says "bouncy toy"), red wears a dark
 *    industrial fuse collar and a tilted warning band around a glowing core
 *    (says "handle with care"), and the husk stays the same faceted chunk so
 *    `detonate()`'s in-place red→husk swap reads as the same object, burned
 *    out. `TRASH_PART_MANIFEST` is the machine-readable statement of that
 *    intent, pinned by test.
 *
 * 3. **Constant draw calls.** Each kind is ONE merged BufferGeometry with
 *    exactly TWO groups (body, trim) rendered by the existing per-kind
 *    InstancedMesh with a two-material array — so the instancing scheme,
 *    `INSTANCE_CAP`, and `syncMeshes` are untouched and the whole restyle
 *    costs five extra draw calls total, not five hundred.
 *
 * 4. **DOM-free and deterministic.** Geometry only — no canvas, no loader, no
 *    randomness — so bun can unit-test the envelopes directly and 18.8's
 *    screenshot baselines see identical pixels every run. (Canvas grain maps
 *    were considered and deliberately deferred: at gameplay distance the
 *    silhouette and the two-tone split do the identity work; a map would add a
 *    DOM dependency to no visible end. If a later polish pass wants them,
 *    textures.ts owns that seam.)
 */
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { TrashKind } from "../simulation/types.js";

/**
 * THE collider envelopes — the single copy. `Trash.spawnAt` builds its
 * ColliderDesc from this table (the review found the previous "mirror"
 * arrangement meant a collider retune could silently strand the visuals
 * outside the real collider with every test green). The numbers are
 * GAMEPLAY-owned; they live beside the factories they bound so the geometry
 * tests can measure against the true values, and physics still never comes
 * from a mesh — this is a table of numbers, and the mesh is bounded by it,
 * not the other way around. Retune a collider HERE; the envelope tests then
 * re-measure every vertex against the new value automatically.
 */
export const TRASH_COLLIDER_ENVELOPES = {
  crate: { kind: "box" as const, halfExtent: 0.425 },
  can: { kind: "cylinder" as const, radius: 0.35, halfHeight: 0.4 },
  green: { kind: "ball" as const, radius: 0.45 },
  red: { kind: "ball" as const, radius: 0.48 },
  husk: { kind: "ball" as const, radius: 0.48 },
};

/**
 * Named parts per kind — the form-carries-meaning contract in data. The test
 * pins that green's and red's manifests differ by more than tint, so a future
 * simplification cannot silently regress the 18.1 colorblind rule back to
 * "two spheres, different paint".
 */
export const TRASH_PART_MANIFEST: Record<TrashKind, readonly string[]> = {
  crate: ["slat-stack", "corner-posts", "edge-rails"],
  can: ["ribbed-body", "lid", "pull-tab", "rolled-rims", "ribs"],
  green: ["bounce-shell", "equator-gasket", "panel-seams"],
  red: ["glow-core", "fuse-collar", "warning-band"],
  husk: ["charred-chunk", "ember-knots"],
};

/** Merged geometry + the group→material contract the InstancedMesh consumes. */
export interface TrashModel {
  geometry: THREE.BufferGeometry;
  /** group 0 = body material slot, group 1 = trim material slot */
  groupCount: 2;
}

const box = (w: number, h: number, d: number, x: number, y: number, z: number): THREE.BufferGeometry =>
  new THREE.BoxGeometry(w, h, d).translate(x, y, z);

/** Equatorial ring (lies flat around the y axis). */
const ring = (radius: number, tube: number, radial: number, tubular: number, y: number): THREE.BufferGeometry =>
  new THREE.TorusGeometry(radius, tube, radial, tubular).rotateX(Math.PI / 2).translate(0, y, 0);

/** Merge parts into one geometry with exactly two groups: [body, trim]. */
function assemble(bodyParts: THREE.BufferGeometry[], trimParts: THREE.BufferGeometry[]): TrashModel {
  const body = mergeGeometries(bodyParts, false)!;
  const trim = mergeGeometries(trimParts, false)!;
  const geometry = mergeGeometries([body, trim], true)!;
  for (const part of [...bodyParts, ...trimParts, body, trim]) part.dispose();
  return { geometry, groupCount: 2 };
}

/**
 * Slatted shipping crate. Body: three horizontal slats with visible gaps (the
 * slat lines read at any distance — this is why no grain map is needed). Trim:
 * four corner posts and eight edge rails that reach the full 0.425 envelope,
 * so the crate still sits flush on the floor exactly where its collider does.
 */
export function createCrateModel(): TrashModel {
  const bodyParts: THREE.BufferGeometry[] = [];
  for (const y of [-0.2675, 0, 0.2675]) bodyParts.push(box(0.78, 0.245, 0.78, 0, y, 0));

  const trimParts: THREE.BufferGeometry[] = [];
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) trimParts.push(box(0.085, 0.85, 0.085, sx * 0.3825, 0, sz * 0.3825));
  for (const sy of [-1, 1]) {
    for (const sz of [-1, 1]) trimParts.push(box(0.85, 0.085, 0.085, 0, sy * 0.3825, sz * 0.3825));
    for (const sx of [-1, 1]) trimParts.push(box(0.085, 0.085, 0.68, sx * 0.3825, sy * 0.3825, 0));
  }
  return assemble(bodyParts, trimParts);
}

/**
 * Ribbed steel can with rolled rims and a pull-tab. The rims stop at y 0.392
 * against the 0.4 half-height envelope — an 8 mm rest gap that is invisible at
 * gameplay distance and the price of the rolled-rim silhouette.
 */
export function createCanModel(): TrashModel {
  const bodyParts = [
    new THREE.CylinderGeometry(0.33, 0.33, 0.72, 18),
    new THREE.CylinderGeometry(0.3, 0.3, 0.02, 18).translate(0, 0.375, 0),
    box(0.1, 0.012, 0.045, 0.06, 0.386, 0),
  ];
  const trimParts = [
    ring(0.315, 0.032, 8, 16, 0.36),
    ring(0.315, 0.032, 8, 16, -0.36),
    ring(0.322, 0.016, 6, 14, 0.13),
    ring(0.322, 0.016, 6, 14, -0.13),
  ];
  return assemble(bodyParts, trimParts);
}

/**
 * Panel-seamed bounce orb: the equator gasket band plus two meridian seams say
 * "moulded rubber toy" in silhouette, not just in tint. Gasket reaches the
 * full 0.45 ball envelope; the shell rests 1 cm inside it.
 */
export function createGreenModel(): TrashModel {
  const bodyParts = [new THREE.SphereGeometry(0.44, 18, 12)];
  const meridianA = new THREE.TorusGeometry(0.42, 0.012, 6, 16); // default torus IS a vertical ring
  const meridianB = new THREE.TorusGeometry(0.42, 0.012, 6, 16).rotateY(Math.PI / 2);
  const trimParts = [ring(0.415, 0.035, 8, 20, 0), meridianA, meridianB];
  return assemble(bodyParts, trimParts);
}

/**
 * Hazard orb: today's glowing core (emissive 1.2 lives on the BODY material,
 * unchanged — the standing danger identity, per the portal-outshine
 * discipline) now wears a dark industrial fuse collar and a tilted warning
 * band. The dark trim carries NO emissive, so the total glowing mass on screen
 * goes slightly DOWN, not up.
 */
export function createRedModel(): TrashModel {
  const bodyParts = [new THREE.SphereGeometry(0.465, 18, 12)];
  // Band ring 0.45 + tube 0.025 = outer 0.475: proud of the 0.465 core along
  // its WHOLE tilted circumference, inside the 0.48 envelope (a torus point
  // never exceeds ring + tube from centre, and rotation preserves norms). The
  // review-era band (0.425 + 0.028 = 0.453) was entirely BURIED inside the
  // core — invisible dead geometry that existed only in the part manifest,
  // which is exactly the narrated-but-unmeasured fiction this slice's tests
  // were rewritten to refuse. The off-equator protrusion test now measures it.
  const band = new THREE.TorusGeometry(0.45, 0.025, 8, 20).rotateX(Math.PI / 2).rotateZ(0.5);
  const trimParts = [ring(0.435, 0.045, 10, 20, 0), band];
  return assemble(bodyParts, trimParts);
}

/**
 * The burned-out husk. Same faceted chunk silhouette as before (detail-1
 * dodecahedron — detail-0's deep facets would visibly float on the 0.48 ball
 * collider), plus three ember knots that keep a faint dying-coal glow on the
 * trim slot. `detonate()` swaps kind red→husk in place, so this must read as
 * the red orb's corpse, not a sixth object.
 */
export function createHuskModel(): TrashModel {
  // Radius 0.48 — the FULL envelope, deliberately. A polyhedron resting on a
  // facet is supported by its face PLANES, not its vertices: detail-1's min
  // face-plane distance is 0.938r, so every centimetre shaved off the radius
  // adds visible facet-rest hover over the 0.48 ball collider. At 0.48 the
  // worst-case hover matches the pre-18.27 husk (~3 cm); at the review-caught
  // 0.47 it was measurably worse than what shipped before this slice.
  const bodyParts = [new THREE.DodecahedronGeometry(0.48, 1)];
  const trimParts: THREE.BufferGeometry[] = [];
  // Knots sit half-buried at radius ~0.33 so their circumradius-0.11 corners
  // stay inside the 0.48 envelope (0.33 + 0.11 = 0.44) — measured by the test.
  for (const [i, [ax, ay]] of [[0.4, 0.9], [2.5, -0.4], [4.4, 0.3]].entries()) {
    const knot = new THREE.TetrahedronGeometry(0.11, 0)
      .rotateY(i * 2.1)
      .translate(Math.cos(ax!) * 0.26, Math.sin(ay!) * 0.2, Math.sin(ax!) * 0.26);
    trimParts.push(knot);
  }
  return assemble(bodyParts, trimParts);
}

/** All five, keyed the way TrashSpawner's instanced meshes are. */
export function createTrashModels(): Record<TrashKind, TrashModel> {
  return {
    crate: createCrateModel(),
    can: createCanModel(),
    green: createGreenModel(),
    red: createRedModel(),
    husk: createHuskModel(),
  };
}
